import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18nPackage from "../../i18n/i18n";
import type { CommonConsumerProps, HeaderRowProps, EditConsumerProps } from "./InstitutionBaseRow";

// Internal use. Explicitly copies only the header-relevant fields so callers can safely
// spread the rest of their props without leaking them through commonProps.
export function pickHeaderRowProps(
	props: CommonConsumerProps,
	label: HeaderRowProps["label"],
	description: HeaderRowProps["description"],
	hint?: HeaderRowProps["hint"],
): HeaderRowProps {
	return { loading: props.loading, label, description, hint };
}

type InstitutionRowHeaderOverrides = { label?: ReactNode; description?: ReactNode; hint?: ReactNode };

// The leaf keys every row's translations are scoped under, i.e. "institutionSettings.information.<key>".
// Kept as a closed union (rather than `string`) so useTranslation's keyPrefix typing - and the "header"/
// "description" lookups below - stay checked against the real translation resource.
type InstitutionRowKey =
	"name" | "origin" | "subdomain" | "institutionId" | "usage" | "product" | "accountType" | "platform" | "yujaAccount";

export function useInstitutionRowHeader(
	key: InstitutionRowKey,
	props: CommonConsumerProps,
	build?: (
		t: ReturnType<typeof useTranslation<undefined, `institutionSettings.information.${InstitutionRowKey}`>>["t"],
	) => InstitutionRowHeaderOverrides,
) {
	const { t } = useTranslation(undefined, {
		keyPrefix: `institutionSettings.information.${key}`,
		i18n: i18nPackage,
	});
	const overrides = build?.(t);
	const commonProps = pickHeaderRowProps(
		props,
		overrides?.label ?? t("header"),
		overrides?.description ?? t("description"),
		overrides?.hint,
	);
	return { t, commonProps };
}

// Internal use. Centralizes the base "disabled while saving or explicitly disabled" rule
export function getDisabledProps(
	props: EditConsumerProps,
	extraDisabled?: boolean,
	tooltipFallback?: string,
): Pick<EditConsumerProps, "disabled" | "disabledTooltip"> {
	return {
		disabled: props.disabled || props.saving || extraDisabled,
		disabledTooltip: props.disabledTooltip ?? tooltipFallback,
	};
}
